/**
 * Central OT protocol configuration.
 *
 * Each entry defines all behaviour for a protocol:
 *   areas            — area dropdown options (as stored in OtConfiguration)
 *   areaAbbrev       — area name → short token used in the full_address 4th segment
 *                      identity entries (area === abbrev) can be omitted; any unknown
 *                      area falls back to the area name itself
 *   addressValidation — { re, hint } for all areas, OR { [area]: { re, hint }, _default: { re, hint } }
 *   supportsSwap     — show byte-order (swap_mode) selector
 *   supportsOffset   — show offset field
 *
 * full_address format: ip$protocol$areaAbbrev$address[:offset]
 */

export const SWAP_MODES = ["AB", "BA", "ABCD", "DCBA", "CDAB", "BADC"];

export const DATA_TYPES = [
  "bool",
  "int8", "uint8", "int16", "uint16", "int32", "uint32",
  "int64", "uint64", "float32", "float64", "string",
];

export const NUMERIC_TYPES = new Set([
  "int8", "uint8", "int16", "uint16", "int32", "uint32",
  "int64", "uint64", "float32", "float64",
]);

// Maps DB zeek_proto → key stored in OT configurations (backward compat) —
// used when resolving the DB-backed `supported_protocols` system config, not
// the static OT_PROTOCOLS map above.
export const ZEEK_TO_STORED_KEY = { "ge-srtp": "ge", "iec104": "iec-104" };

// Shapes one `supported_protocols` system-config entry (as returned by
// API.getSystemConfig("ot", "supported_protocols")) into the protoConfig
// shape validateAddressFromConfig()/the tag-editing UI expect — the DB-backed
// equivalent of an OT_PROTOCOLS[key] entry above, since protocol metadata is
// configured per-tenant rather than hardcoded here.
export function resolveProtocolConfig(dbProto) {
  const zeekProto = dbProto.zeek_proto;
  const storedKey = ZEEK_TO_STORED_KEY[zeekProto] ?? zeekProto;
  const tagConf = dbProto.tag_config;
  return {
    key: storedKey,
    label: dbProto.label,
    zeek_proto: zeekProto,
    areas: tagConf?.areas ?? [],
    areaAbbrev: tagConf?.area_abbrev ?? {},
    addressValidation: tagConf?.address_validation ?? null,
    supportsSwap: tagConf?.supports_swap ?? false,
    supportsOffset: tagConf?.supports_offset ?? false,
    vendor: dbProto.vendor ?? null,
    standard: dbProto.standard ?? null,
    categories: dbProto.categories ?? [],
    default_ports: dbProto.default_ports ?? [],
    devices: dbProto.devices ?? null,
  };
}

export const OT_PROTOCOLS = {
  modbus: {
    label: "Modbus",
    areas: ["coil", "discrete_input", "input_register", "holding_register"],
    areaAbbrev: {
      // identity — no abbreviation; all area names pass through as-is
    },
    addressValidation: {
      re:   /^\d+$/,
      hint: "Integer register (e.g. 0, 100)",
    },
    supportsSwap:   true,
    supportsOffset: true,
  },

  s7: {
    label: "S7 (TCP)",
    areas: ["input", "output", "memory", "db"],
    areaAbbrev: {
      input:  "i",
      output: "q",
      memory: "m",
      // db → db (identity, no entry needed)
    },
    addressValidation: {
      db: {
        re:   /^[Dd][Bb]\d+\.([Dd][Bb][WwDdBbXx])?\d+(\.[0-7])?$/,
        hint: "DB address  e.g. DB1.DBW0  or  DB2.DBX4.1",
      },
      _default: {
        re:   /^[A-Za-z]*\d+(\.[0-7])?$/,
        hint: "Byte  or  byte.bit (0-7)  e.g. 0  or  0.3",
      },
    },
    supportsSwap:   false,
    supportsOffset: true,
  },

  opcua: {
    label: "OPC-UA",
    areas: ["node", "value", "data_item", "event", "method"],
    areaAbbrev: {},
    addressValidation: {
      re:   /^(ns=\d+;)?(i=\d+|s=[^;]+|g=[0-9a-fA-F-]+|b=[A-Za-z0-9+/=]+)$/,
      hint: "NodeId — ns=X;i=N  |  ns=X;s=Name  |  i=N  |  s=Name",
    },
    supportsSwap:   false,
    supportsOffset: false,
  },

  fins: {
    label: "Omron FINS",
    areas: ["dm", "cio", "wr", "hr", "ar"],
    areaAbbrev: {},
    addressValidation: {
      re:   /^\d+$/,
      hint: "Integer word address (e.g. 0, 100)",
    },
    supportsSwap:   false,
    supportsOffset: false,
  },

  "enip-cip": {
    label: "EtherNet/IP CIP",
    areas: ["tag", "assembly", "instance"],
    areaAbbrev: {},
    addressValidation: {
      tag: {
        re:   /^[A-Za-z_][A-Za-z0-9_[\].]*$/,
        hint: "Tag name  e.g. MyTag  or  MyArray[0].Field",
      },
      _default: {
        re:   /^\d+$/,
        hint: "Integer (e.g. 0, 100)",
      },
    },
    supportsSwap:   false,
    supportsOffset: false,
  },

  ge: {
    label: "GE SRTP",
    areas: ["r", "ai", "aq", "i", "q", "m", "g", "t"],
    areaAbbrev: {},
    addressValidation: {
      re:   /^\d+$/,
      hint: "Integer register address (e.g. 0, 100)",
    },
    supportsSwap:   false,
    supportsOffset: false,
  },

  "iec-104": {
    label: "IEC 60870-5-104",
    areas: ["single_point", "double_point", "measured_normalized", "measured_scaled", "measured_float"],
    areaAbbrev: {
      single_point:        "sp",
      double_point:        "dp",
      measured_normalized: "mn",
      measured_scaled:     "ms",
      measured_float:      "mf",
    },
    addressValidation: {
      re:   /^\d+$/,
      hint: "Information Object Address (e.g. 1, 2048)",
    },
    supportsSwap:   false,
    supportsOffset: false,
  },

  mqtt: {
    label: "MQTT",
    areas: ["topic"],
    areaAbbrev: {},
    addressValidation: {
      // eslint-disable-next-line no-control-regex -- intentional: matches a null byte in binary protocol data
      re:   /^[^\u0000]+$/,
      hint: "Topic path (e.g. sensors/temperature)",
    },
    supportsSwap:   false,
    supportsOffset: false,
  },
};

/** Ordered list of protocol keys for dropdowns. */
export const PROTOCOL_KEYS = Object.keys(OT_PROTOCOLS);

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Returns the address validation rule for a given protocol + area.
 * Rule shape: { re: RegExp, hint: string } | null
 */
function getAddressRule(protocol, area) {
  const proto = OT_PROTOCOLS[protocol];
  if (!proto) return null;
  const v = proto.addressValidation;
  if (!v) return null;
  // Per-area structure has no `re` at the top level
  if (v.re) return v;
  return v[area] ?? v._default ?? null;
}

/**
 * Validates the address for the given protocol/area.
 * Returns an error hint string on failure, null when valid (or address is empty).
 */
export function validateAddress(protocol, area, address) {
  if (!address) return null;
  const rule = getAddressRule(protocol, area);
  if (!rule) return null;
  return rule.re.test(address) ? null : rule.hint;
}

/**
 * Validates the address using a resolved protocol config object (from DB or static).
 * Accepts both RegExp and string for the `re` field (DB stores strings).
 */
export function validateAddressFromConfig(protoConfig, area, address) {
  if (!address || !protoConfig?.addressValidation) return null;
  const v = protoConfig.addressValidation;
  let rule;
  if (v.re !== undefined) {
    rule = v;
  } else {
    rule = v[area] ?? v._default ?? null;
  }
  if (!rule) return null;
  const re = typeof rule.re === "string" ? new RegExp(rule.re) : rule.re;
  return re.test(address) ? null : rule.hint;
}

/**
 * Resolves the area abbreviation for use in full_address.
 * Falls back to the area name itself when no abbreviation is defined.
 */
export function getAreaAbbrev(protocol, area) {
  return OT_PROTOCOLS[protocol]?.areaAbbrev?.[area] ?? area;
}

/**
 * Resolves an area abbreviation back to its full area name.
 * Falls back to the abbrev itself.
 */
export function abbrevToArea(protocol, abbrev) {
  const proto = OT_PROTOCOLS[protocol];
  if (!proto) return abbrev;
  const entry = Object.entries(proto.areaAbbrev).find(([, v]) => v === abbrev);
  return entry ? entry[0] : abbrev;
}

/**
 * Builds a full_address string from its components.
 * Format: ip$protocol$areaAbbrev$address[:offset]
 */
export function buildFullAddress(ip, protocol, area, address, offset) {
  const abbrev = getAreaAbbrev(protocol, area);
  const base = [ip, protocol, abbrev, address].join("$");
  return offset ? `${base}:${offset}` : base;
}

/**
 * Parses a full_address string into its components.
 * Returns { ip, protocol, areaAbbrev, address, offset }
 * The trailing :N is treated as offset only when N is a plain integer.
 */
export function parseFullAddress(full_address) {
  if (!full_address) return {};
  const [ip, protocol, areaAbbrev, ...rest] = full_address.split("$");
  const addrWithOffset = rest.join("$");
  const colonIdx = addrWithOffset.lastIndexOf(":");
  const hasOffset =
    colonIdx !== -1 && /^\d+$/.test(addrWithOffset.slice(colonIdx + 1));
  return {
    ip,
    protocol,
    areaAbbrev,
    address: hasOffset
      ? addrWithOffset.slice(0, colonIdx)
      : addrWithOffset,
    offset: hasOffset ? addrWithOffset.slice(colonIdx + 1) : null,
  };
}
